import * as React from 'react';
import type { MapRenderer } from './bpmn-renderer';
import type { CanvasSnapshot, Markers } from './map-logic';
import './map-canvas.css';

/**
 * The map canvas (T-0189): bpmn-js in a bordered card, the same for
 * engagement maps and the framework's reference processes. The parent
 * passes the BPMN to draw (generated by code), the marks, and whether it is
 * editable; bpmn-js is loaded on first use.
 *
 * Nothing is rendered inside the canvas but bpmn-js itself, so no panel,
 * notice or tag can cover the diagram or the bpmn.io watermark (OQ-V).
 * `redraw` draws the same BPMN again, to put the canvas back after a
 * refused gesture.
 *
 * The full-screen map is the one place something floats over the diagram
 * (the chat, a step's card): `overlay`. This component places it, in a
 * layer that ends above the bottom right corner, so whatever a screen
 * passes can never cover the bpmn.io watermark, which the licence requires
 * to stay fully visible. `handle` gives the screen the zoom buttons.
 */
/**
 * bpmn-js is loaded once, on first use, and shared by every canvas on the
 * page (Compare with today draws two at once, T-0208).
 */
let rendererModule: Promise<typeof import('./bpmn-renderer')> | null = null;
function loadRenderer(): Promise<typeof import('./bpmn-renderer')> {
  rendererModule ??= import('./bpmn-renderer').catch((err: unknown) => {
    rendererModule = null;
    throw err;
  });
  return rendererModule;
}

/** What a screen can ask of the canvas: the zoom buttons of the full-screen map. */
export interface MapCanvasHandle {
  zoomIn(): void;
  zoomOut(): void;
  /** Fit the whole map to the space. */
  fit(): void;
}

export interface MapCanvasProps {
  xml: string;
  markers: Markers;
  editable: boolean;
  redraw?: number;
  label: string;
  onSelect: (elementId: string) => void;
  onCanvasChange?: (canvas: CanvasSnapshot) => void;
  handle?: React.Ref<MapCanvasHandle>;
  /** Floats over the diagram, clear of the bpmn.io watermark (the full-screen map only). */
  overlay?: React.ReactNode;
}

export function MapCanvas({
  xml,
  markers,
  editable,
  redraw = 0,
  label,
  onSelect,
  onCanvasChange,
  handle,
  overlay,
}: MapCanvasProps) {
  const hostRef = React.useRef<HTMLDivElement>(null);
  const rendererRef = React.useRef<MapRenderer | null>(null);
  const latest = React.useRef({ xml, markers, onSelect, onCanvasChange });
  latest.current = { xml, markers, onSelect, onCanvasChange };
  const [ready, setReady] = React.useState(0);
  const [failed, setFailed] = React.useState(false);

  // One renderer per mode: the viewer to read, the modeler to edit.
  React.useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let alive = true;
    let created: MapRenderer | null = null;
    setFailed(false);
    loadRenderer()
      .then((m) =>
        m.createMapRenderer({
          container: host,
          editable,
          onElementClick: (id) => latest.current.onSelect(id),
          onCanvasChange: (snap) => latest.current.onCanvasChange?.(snap),
        }),
      )
      .then(async (r) => {
        if (!alive) {
          r.destroy();
          return;
        }
        created = r;
        rendererRef.current = r;
        r.setMarkers(latest.current.markers);
        await r.show(latest.current.xml);
        if (alive) setReady((n) => n + 1);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
      created?.destroy();
      rendererRef.current = null;
    };
  }, [editable]);

  const first = React.useRef(true);
  React.useEffect(() => {
    // The renderer draws the current BPMN when it is created.
    if (first.current) {
      first.current = false;
      return;
    }
    void rendererRef.current?.show(xml).catch(() => setFailed(true));
  }, [xml, redraw]);

  React.useEffect(() => {
    rendererRef.current?.setMarkers(markers);
  }, [markers, ready]);

  React.useImperativeHandle(
    handle,
    () => ({
      zoomIn: () => rendererRef.current?.zoom(1),
      zoomOut: () => rendererRef.current?.zoom(-1),
      fit: () => rendererRef.current?.fit(),
    }),
    [],
  );

  React.useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => rendererRef.current?.resized());
    ro.observe(host);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="relative flex min-h-[360px] min-w-0 flex-1 flex-col px-4 py-2">
      <div
        ref={hostRef}
        role="region"
        aria-label={label}
        data-testid="map-canvas"
        data-editable={editable ? 'true' : undefined}
        className="map-canvas-host relative isolate min-h-0 flex-1 overflow-hidden rounded-card border border-ink-100 bg-paper-0"
      >
        {failed ? (
          <div className="flex h-full flex-col items-center justify-center gap-1 p-6 text-center text-body-sm text-ink-500">
            <strong className="font-semibold text-ink-900">The map could not be drawn</strong>
            The BPMN drawing library did not load. Reload the page to try again.
          </div>
        ) : null}
      </div>
      {overlay ? (
        // Ends 12px above the bpmn.io watermark (15px in from the corner of
        // the canvas, about 21px high), so nothing here can cover it.
        <div
          data-testid="map-canvas-overlay"
          className="pointer-events-none absolute bottom-14 right-7 top-5 z-10 flex flex-col items-end justify-end"
        >
          <div className="pointer-events-auto flex max-h-full min-h-0 flex-col items-end">
            {overlay}
          </div>
        </div>
      ) : null}
    </div>
  );
}
